import { CSSProperties } from 'react';
import { Editor as ReactEditor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import './customStyles.css';
import * as Sentry from "@sentry/react";
import { styx, clsx } from 'modules/Utils';

type ProgressFn = (percent: number) => void;

interface BlobInfo {
    id: () => string;
    name: () => string;
    filename: () => string;
    blob: () => Blob;
    base64: () => string;
    blobUri: () => string;
    uri: () => string | undefined;
}

type UploadHandler = (
    blobInfo: BlobInfo,
    progress: ProgressFn
) => Promise<string>;

const cloudinaryUploadHandler: UploadHandler = (
    blobInfo: BlobInfo,
    progress: ProgressFn
) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.withCredentials = false;
        xhr.open(
            "POST",
            process.env.REACT_APP_CLOUDINARY_IMAGE_UPLOAD_URL as string
        );

        xhr.upload.onprogress = (e) => {
            progress((e.loaded / e.total) * 100);
        };

        xhr.onload = () => {
            if (xhr.status === 403) {
                Sentry.captureException({
                    message: `HTTP Error: ${xhr.status}`,
                    remove: true,
                });
                return reject({ message: `HTTP Error: ${xhr.status}`, remove: true });
            }

            if (xhr.status < 200 || xhr.status >= 300) {
                Sentry.captureException(`HTTP Error: ${xhr.status}`);
                return reject(`HTTP Error: ${xhr.status}`);
            }

            const json = JSON.parse(xhr.responseText);

            if (!json || typeof json.secure_url !== "string") {
                Sentry.captureException(`Invalid JSON: ${xhr.responseText}`);
                return reject(`Invalid JSON: ${xhr.responseText}`);
            }

            resolve(json.secure_url);
        };

        xhr.onerror = () => {
            Sentry.captureException(
                `Image upload failed due to a XHR Transport error. Code: ${xhr.status}`
            );
            reject(
                `Image upload failed due to a XHR Transport error. Code: ${xhr.status}`
            );
        };

        const formData = new FormData();
        formData.append("file", blobInfo.blob(), blobInfo.filename());
        formData.append("upload_preset", "unsigned-assets");

        xhr.send(formData);
    });

const INITIAL_VALUE = "<p>This is the initial content of the editor.</p>";
const FONT_FAMILY_STYLE = "font-family:DIN,Helvetica,Arial,sans-serif;";

interface CustomTinyMCEEditorProps {
    className?: string;
    style?: CSSProperties;
    saveTemplate: (template?: string) => void;
    apiKey?: string;
    initialValue?: string;
    isSMSMode?: boolean;
}

const CustomTinyMCEEditor = ({
    className,
    style,
    saveTemplate,
    apiKey,
    initialValue,
    isSMSMode
}: CustomTinyMCEEditorProps) => {
    const editorConfig = {
        menubar: false,
        toolbar: isSMSMode ? false : 'bold italic underline link blockquote bullist numlist alignleft aligncenter alignright alignjustify',
        plugins: ['link', 'lists'],
        content_style: `
      body { 
        font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif;
        padding: 12px 16px !important;
        margin: 0 !important;
        background: transparent !important;
      }
      p {
        margin: 0 0 8px 0 !important;
      }
      a {
        color: #8800FF !important;
        text-decoration: none !important;
      }
    `,
        statusbar: false,
        setup: (editor: TinyMCEEditor) => {
            if (isSMSMode) {
                editor.on('KeyUp', () => {
                    const content = editor.getContent({ format: 'text' });
                    if (content.length > 500) {
                        const trimmedContent = content.slice(0, 500);
                        editor.setContent(trimmedContent);

                        editor.selection.select(editor.getBody(), true);
                        editor.selection.collapse(false);
                    }
                });

                editor.on('PastePreProcess', (e) => {
                    const currentContent = editor.getContent({ format: 'text' });
                    const pastedText = e.content;
                    const totalLength = currentContent.length + pastedText.length;

                    if (totalLength > 500) {
                        const remainingChars = 500 - currentContent.length;
                        if (remainingChars > 0) {
                            e.content = pastedText.slice(0, remainingChars);
                        } else {
                            e.content = '';
                        }
                    }
                });
            }
        }
    };

    const handleSaveTemplate = (template: string, editor: TinyMCEEditor) => {
        const bookmark = editor.selection.getBookmark(2, true);

        if (saveTemplate) {
            if (isSMSMode) {
                const content = editor.getContent({ format: 'text' });
                if (content.length > 500) {
                    template = content.slice(0, 500);
                    editor.setContent(template);
                }
            }
            saveTemplate(template);
        }

        editor.selection.moveToBookmark(bookmark);
    };

    return (
        <div
            className={clsx('customTinyMCEEditorComponent', 'editor-container', className)}
            style={styx(style)}
            data-sms-mode={isSMSMode}
        >
            <ReactEditor
                apiKey={apiKey}
                initialValue={initialValue}
                init={editorConfig}
                onEditorChange={handleSaveTemplate}
            />
        </div>
    );
};

const sty: Record<string, CSSProperties> = {}

export default CustomTinyMCEEditor;
